import type { Readable } from "svelte/store";
import { derived, writable } from "svelte/store";
import { localStorageWritable } from "./localStorageWritable";

type Coordinates = [number, number];

export interface PullFactor {
	id: number;
	name: string;
	checked: boolean;
}

interface EmploymentType {
	name: string;
	searchTerm: string;
	checked: boolean;
}

export interface Sector {
	id: string;
	name: string;
	checked: boolean;
}

// Function to check if the stored sectors are outdated
const isSectorsStorageOutdated = (
	storedSectors: Sector[],
	defaultSectors: Sector[]
): boolean => {
	// If lengths don't match, consider it outdated
	if (storedSectors.length !== defaultSectors.length) {
		return true;
	}

	// Check if all sector IDs in the default sectors exist in the stored sectors
	const storedSectorIds = new Set(storedSectors.map((sector) => sector.id));
	for (const defaultSector of defaultSectors) {
		if (!storedSectorIds.has(defaultSector.id)) {
			return true;
		}
	}

	// Check if any sector has a numeric ID (from old format)
	for (const sector of storedSectors) {
		if (typeof sector.id === "number") {
			return true;
		}
	}

	return false;
};

// Initialize the store with default values
export const query = writable("");
export const queryID = writable<string>("");
export const lastQuery = localStorageWritable<string>("lastQuery", "");
export const locationPrompted = writable<boolean>(false);

export const locationQuery = localStorageWritable<string>("locationQuery", "");
export const locationInputQuery = localStorageWritable<string>(
	"locationInputQuery",
	""
);

export const coordinates = localStorageWritable<Coordinates>(
	"coordinates",
	[52.083333, 5.116667]
);

export const distance = localStorageWritable<[number]>("distance", [100]);

// Any time the coordinates or distance stores change, a polygon gets generated that resembles a circle at a certain distance from the coordinates.
// This polygon is used to filter the search results.
export const coordinatesPolygon: Readable<number[]> = derived(
	[coordinates, distance],
	([$coordinates, $distance]) => {
		const [lat, lng] = $coordinates;
		const earthRadius = 6371; // Earth's radius in km
		const numPoints = 36; // Number of points in the polygon (for a smooth circle)
		const polygon = [];

		// Convert distance from km to radians
		const angularDistance = $distance[0] / earthRadius;

		for (let i = 0; i < numPoints; i++) {
			// Calculate the bearing for each point
			const bearing = ((i * 360) / numPoints) * (Math.PI / 180); // Convert degrees to radians

			// Calculate the latitude and longitude of each point
			const pointLat = Math.asin(
				Math.sin((lat * Math.PI) / 180) * Math.cos(angularDistance) +
					Math.cos((lat * Math.PI) / 180) *
						Math.sin(angularDistance) *
						Math.cos(bearing)
			);
			const pointLng =
				(lng * Math.PI) / 180 +
				Math.atan2(
					Math.sin(bearing) *
						Math.sin(angularDistance) *
						Math.cos((lat * Math.PI) / 180),
					Math.cos(angularDistance) -
						Math.sin((lat * Math.PI) / 180) * Math.sin(pointLat)
				);

			// Convert from radians back to degrees and round to 4 decimal places
			polygon.push(
				parseFloat(((pointLat * 180) / Math.PI).toFixed(4)),
				parseFloat(((pointLng * 180) / Math.PI).toFixed(4))
			);
		}

		return polygon;
	}
);

export const employmentTypes = localStorageWritable<EmploymentType[]>(
	"employmentTypes",
	[
		{
			name: "Fulltime",
			searchTerm: "FULL_TIME",
			checked: true
		},
		{
			name: "Parttime",
			searchTerm: "PART_TIME",
			checked: true
		},
		{ name: "Stage", searchTerm: "INTERN", checked: true },
		{
			name: "Tijdelijk",
			searchTerm: "TEMPORARY",
			checked: true
		}
	]
);

export const salary = localStorageWritable<[number]>("salary", [0]);

export const pullFactors = localStorageWritable<PullFactor[]>("pullFactors", [
	{ id: 1, name: "Salaris", checked: false },
	{ id: 2, name: "Werksfeer", checked: false },
	{ id: 3, name: "Taken", checked: false },
	{ id: 4, name: "Arbeidsvoorwaarden", checked: false },
	{ id: 5, name: "Vast contract", checked: false }
]);

// Define the default sectors
export const defaultSectors: Sector[] = [
	{
		id: "AGRICULTURE_GREEN",
		name: "Agrarisch en groen",
		checked: false
	},
	{
		id: "AUTOMOTIVE",
		name: "Autobranche",
		checked: false
	},
	{
		id: "SECURITY",
		name: "Beveiliging",
		checked: false
	},
	{
		id: "CONSTRUCTION",
		name: "Bouw",
		checked: false
	},
	{
		id: "CULTURE_SPORTS_RECREATION",
		name: "Recreatie, sport en cultuur",
		checked: false
	},
	{
		id: "RETAIL",
		name: "Detailhandel",
		checked: false
	},
	{
		id: "ECONOMIC_ADMINISTRATIVE",
		name: "Economisch-administratief",
		checked: false
	},
	{
		id: "FINANCIAL_SERVICES",
		name: "Financiële dienstverlening",
		checked: false
	},
	{
		id: "WHOLESALE",
		name: "Groothandel",
		checked: false
	},
	{
		id: "HOSPITALITY",
		name: "Horeca",
		checked: false
	},
	{
		id: "ICT",
		name: "ICT-beroepen",
		checked: false
	},
	{
		id: "INDUSTRY",
		name: "Industrie",
		checked: false
	},
	{
		id: "CUSTOMER_SERVICE",
		name: "Klantcontact",
		checked: false
	},
	{
		id: "CLIMATE",
		name: "Klimaat",
		checked: false
	},
	{
		id: "EDUCATION",
		name: "Onderwijs",
		checked: false
	},
	{
		id: "GOVERNMENT",
		name: "Overheid",
		checked: false
	},
	{
		id: "CLEANING",
		name: "Schoonmaak",
		checked: false
	},
	{
		id: "SOCIAL_WORK_YOUTH_CHILDCARE",
		name: "Sociaal werk, jeugdzorg en kinderopvang",
		checked: false
	},
	{
		id: "TRANSPORT_LOGISTICS",
		name: "Transport en logistiek",
		checked: false
	},
	{
		id: "HEALTHCARE",
		name: "Zorg",
		checked: false
	}
];

// Create the sectors store with the isStorageOutdated function
export const sectors = localStorageWritable<Sector[]>(
	"sectors",
	defaultSectors,
	isSectorsStorageOutdated
);

export const session = localStorageWritable<boolean>("session", false);
